import {
    CurrentTransportLegParty,
    PartyExternalIdentifiers,
    Shipment,
    ShipmentStatus,
    TransportOrder
} from "@/model/transportorder/transportOrder"

const notCancelled = (shipment: Shipment): boolean => shipment.status !== ShipmentStatus.CANCELLED

export interface TransportChainLocation {
    party: CurrentTransportLegParty
    externalIdentifiers: PartyExternalIdentifiers,
    dateTime?: string,
    dateTimeCountryCode?: string,
}

export const getTableShipments = (shipments: Shipment[]): Shipment[] => {
    const nonCancelledShipments = shipments.filter(notCancelled)
    return nonCancelledShipments.length > 0 ? nonCancelledShipments : shipments
}

export const getEarliestLoadingTimeWindow = (transportOrder: TransportOrder): string | undefined => {
    const timeFroms = getTableShipments(transportOrder._embedded?.shipments ?? [])
        .map(it =>
            it.currentTransportLeg.departureRequestedAt?.time_from
            ?? (it.isStartOfTransportChain ? it.startOfTransportChain.pickupRequestedAt.time_from : undefined)
        )
        .filter((it): it is string => it !== undefined)

    return timeFroms.length === 0
        ? undefined
        : timeFroms.reduce((a, b) => {
            return new Date(a).getTime() < new Date(b).getTime() ? a : b
        })
}

type WithDateTime = Required<Pick<TransportChainLocation, "dateTime">> & TransportChainLocation

export const getEarliestTransportChainPickupLocation = (
    transportOrders: Shipment[]
): TransportChainLocation | undefined => {
    if (transportOrders.length === 0) {
        return undefined
    }

    const transportOrderLocations = transportOrders.map(getTransportOrderPickupLocation)
    const locationsWithDate = transportOrderLocations.filter((it): it is WithDateTime => it.dateTime !== undefined)
    return locationsWithDate.length === 0 ? transportOrderLocations[0] : locationsWithDate
        .reduce((a, b) => new Date(a.dateTime).getTime() < new Date(b.dateTime).getTime() ? a : b)
}

export const getLatestTransportChainDeliveryLocation = (
    transportOrders: Shipment[]
): TransportChainLocation | undefined => {
    if (transportOrders.length === 0) {
        return undefined
    }

    const transportOrderLocations = transportOrders.map(getTransportOrderDeliveryLocation)
    const locationsWithDate = transportOrderLocations.filter((it): it is WithDateTime => it.dateTime !== undefined)

    return locationsWithDate.length === 0 ? transportOrderLocations[0] : locationsWithDate
        .reduce((a, b) => new Date(a.dateTime).getTime() >= new Date(b.dateTime).getTime() ? a : b)
}

const getTransportOrderPickupLocation = (transportOrder: Shipment): TransportChainLocation => {
    return {
        dateTime: transportOrder.currentTransportLeg.departureRequestedAt?.time_from
            ?? (transportOrder.isStartOfTransportChain ? transportOrder.startOfTransportChain.pickupRequestedAt?.time_from : undefined),
        dateTimeCountryCode: transportOrder.currentTransportLeg.placeOfDeparture.address?.country
            ?? (transportOrder.isStartOfTransportChain ? transportOrder.startOfTransportChain.consignor.address?.country : undefined),
        party: transportOrder.currentTransportLeg.placeOfDeparture,
        externalIdentifiers: transportOrder.isStartOfTransportChain
            ? transportOrder.startOfTransportChain.consignor.externalIdentifiers
            : transportOrder.currentTransportLeg.placeOfDeparture.externalIdentifiers,
    }
}

const getTransportOrderDeliveryLocation = (transportOrder: Shipment): TransportChainLocation => {
    return {
        dateTime: transportOrder.currentTransportLeg.arrivalEstimatedAt?.time_to
            ?? (transportOrder.isEndOfTransportChain ? transportOrder.endOfTransportChain.deliveryEstimatedAt?.time_to : undefined),
        dateTimeCountryCode: transportOrder.currentTransportLeg.placeOfArrival.address?.country
            ?? (transportOrder.isEndOfTransportChain ? transportOrder.endOfTransportChain.consignee.address?.country : undefined),
        party: transportOrder.currentTransportLeg.placeOfArrival,
        externalIdentifiers: transportOrder.isEndOfTransportChain
            ? transportOrder.endOfTransportChain.consignee.externalIdentifiers
            : transportOrder.currentTransportLeg.placeOfArrival.externalIdentifiers,
    }
}
